import {
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessSubcategory,
  LifeReadinessQuestionUserLevel,
  QuestionRowProps,
} from '@backrs_ui/common-ui';

import { QuestionnaireProgressTabProps } from '@backrs_ui/common-ui/src/atoms/QuestionnaireProgressTab';

export const LEVEL_MAP: Record<string, 1 | 2 | 3 | 4> = {
  level_one: 1,
  level_two: 2,
  level_three: 3,
  level_four: 4,
};

/**
 * Formats question answers for QuestionRowProps with selected levels.
 */
const getAnswers = (
  question: LifeReadinessQuestion,
  userLevelMap: Record<string, number>,
): QuestionRowProps['answers'] => {
  return [
    {
      key: 'level_one',
      value: question.level_one,
      isSelected: userLevelMap[question.id] === 1,
    },
    {
      key: 'level_two',
      value: question.level_two,
      isSelected: userLevelMap[question.id] === 2,
    },
    {
      key: 'level_three',
      value: question.level_three,
      isSelected: userLevelMap[question.id] === 3,
    },
    {
      key: 'level_four',
      value: question.level_four,
      isSelected: userLevelMap[question.id] === 4,
    },
  ];
};

/**
 *  Builds the tabs props.
 *  1. Determines whether the category has been fully answered, thus should be highlighted.
 *  2. Determines whether the category is the current active category, thus should be marked active.
 */
export const getProgressTabsProps = (
  categories: LifeReadinessCategory[],
  subcategories: LifeReadinessSubcategory[],
  questions: LifeReadinessQuestion[],
  userLevelMap: Record<string, number>,
  currentCategory: LifeReadinessCategory,
): QuestionnaireProgressTabProps[] => {
  return categories.map((category) => {
    // find all subcategories that belong to the current category
    const categorySubcategories = subcategories.filter(
      (s) => s.life_survey_category_id === category.id,
    );

    // find all questions belonging to the current category
    const categoryQuestions = questions.filter((q) =>
      categorySubcategories.some((s) => s.id === q.life_survey_subcategory_id),
    );

    // determine if all questions in the category have been answered
    const allQuestionsAnswered =
      categoryQuestions.length > 0 &&
      categoryQuestions.every((q) => userLevelMap[q.id] !== undefined);

    const isCategoryActive = category.slug === currentCategory.slug;

    return {
      title: category.name,
      subcategories: categorySubcategories.map((subcat) => ({
        id: subcat.name.toLowerCase(),
        label: subcat.name,
      })),
      highlighted: allQuestionsAnswered,
      active: isCategoryActive,
    };
  });
};

/**
 * Get the questions for a subcategory, and transform to the format expected by the QuestionView component.
 */
export const getQuestionsForSubcategory = (
  questions: LifeReadinessQuestion[],
  subcategoryId: number,
  userLevelMap: Record<string, number>,
  handleAnswerChange: (question: LifeReadinessQuestion, key: string) => void,
): Omit<QuestionRowProps, 'toggleExpand' | 'expanded' | 'isFinalQuestion'>[] => {
  return questions
    .filter((q) => q.life_survey_subcategory_id === subcategoryId)
    .map((q) => ({
      title: q.name,
      description: q.description,
      onAnswerChange: (key: string) => handleAnswerChange(q, key),
      answers: getAnswers(q, userLevelMap),
      // show separation line for the last 'knowledge' question on the achievment category
      showSeparation: q.slug === 'opportunity-pathfinding-role-negotiation',
    }));
};

/**
 * Checks if all life readiness questions have been answered by the user
 * @param questions - Array of all life readiness questions
 * @param userLevels - Array of user's answered questions with their levels
 * @returns true if all questions have been answered, false otherwise
 */
export const areAllLifeReadinessQuestionsAnswered = (
  questions: LifeReadinessQuestion[],
  userLevels: LifeReadinessQuestionUserLevel[],
): boolean => {
  return (
    questions.length > 0 &&
    questions.every((question) =>
      userLevels.some((level) => level.life_survey_question_id === question.id),
    )
  );
};

/**
 * get subcategories for a category
 */
export const getSubcategoriesForCategory = (
  subcategories: LifeReadinessSubcategory[],
  categoryId: number,
): LifeReadinessSubcategory[] => {
  return subcategories.filter((s) => s.life_survey_category_id === categoryId);
};
